import {useState} from 'react';
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogBody} from '@/components/ui/dialog.tsx';
import {Button} from '@/components/ui/button.tsx';
import {Input} from '@/components/ui/input.tsx';
import {Label} from '@/components/ui/label.tsx';
import {UserPlusIcon, CheckCircleIcon, ExclamationTriangleIcon} from '@heroicons/react/24/outline';
import {Spinner} from '@/components/ui/spinner.tsx';
import {CustomerSystem, CustomerUserFull} from '@/store/customerFile.ts';

interface Props {
    open: boolean;
    onClose: () => void;
    systems: CustomerSystem[];
    currentUserRole?: number;
    onCreated?: (user: CustomerUserFull) => void;
}

const ROLE_OPTIONS = [
    {value: 1, label: 'בעלים'},
    {value: 2, label: 'מנהל'},
    {value: 3, label: 'משתמש רגיל'},
];

export function InviteUserModal({open, onClose, systems, currentUserRole, onCreated}: Props) {
    const [email, setEmail] = useState('');
    const [role, setRole] = useState(3);
    const [selectedSystemIds, setSelectedSystemIds] = useState<number[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState('');
    const isManager = currentUserRole === 2;
    const roleOptions = isManager ? ROLE_OPTIONS.filter((option) => option.value !== 1) : ROLE_OPTIONS;

    function handleClose() {
        setEmail('');
        setRole(3);
        setSelectedSystemIds([]);
        setSubmitting(false);
        setDone(false);
        setError('');
        onClose();
    }

    async function handleSubmit() {
        setError('');
        const trimmed = email.trim();
        if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
            setError('נא להזין כתובת מייל תקינה');
            return;
        }
        if (isManager && role === 1) {
            setError('מנהל לא יכול להזמין משתמש ברמת בעלים');
            return;
        }
        if (role === 3 && selectedSystemIds.length === 0) {
            setError('יש לבחור לפחות מערכת אחת למשתמש רגיל');
            return;
        }
        setSubmitting(true);
        try {
            const resp = await fetch('/api/customer-file/invite-user/', {
                method: 'POST',
                headers: {'Content-Type': 'application/json', 'X-CSRFToken': getCsrfToken()},
                body: JSON.stringify({
                    email: trimmed,
                    role,
                    user_school_ids: role === 3 ? selectedSystemIds : [],
                }),
            });
            const data = await resp.json();
            if (!resp.ok || !data.status) {
                setError(data.message || 'שגיאה בשליחת ההזמנה');
                return;
            }
            if (data.data) {
                onCreated?.(data.data as CustomerUserFull);
            }
            setDone(true);
        } catch {
            setError('שגיאת רשת, נסה שוב');
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
            <DialogContent dir="rtl" className="max-w-sm">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <UserPlusIcon className="w-5 h-5 text-blue-600" />
                        הזמנת משתמש
                    </DialogTitle>
                </DialogHeader>

                {done ? (
                    <DialogBody className="flex flex-col items-center gap-3 py-6">
                        <CheckCircleIcon className="w-12 h-12 text-green-500" />
                        <p className="text-sm font-medium text-gray-800">ההזמנה נשלחה בהצלחה</p>
                        <p className="text-xs text-gray-500">מייל הזמנה נשלח אל <strong>{email}</strong>.</p>
                        <p className="text-xs text-gray-400">המשתמש יופיע ברשימה עם סטטוס "נשלחה הזמנה".</p>
                        <Button onClick={handleClose} className="mt-2">סגור</Button>
                    </DialogBody>
                ) : (
                    <DialogBody className="space-y-4 py-2">
                        <p className="text-sm text-gray-600">
                            הזן כתובת מייל של המשתמש שברצונך להזמין לתיק לקוח זה. ישלח מייל עם קישור הזמנה.
                        </p>
                        <div className="space-y-1.5">
                            <Label htmlFor="invite-email">כתובת מייל</Label>
                            <Input
                                id="invite-email"
                                type="email"
                                dir="ltr"
                                placeholder="user@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                                disabled={submitting}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="invite-role">רמת גישה</Label>
                            <select
                                id="invite-role"
                                value={role}
                                onChange={(e) => setRole(Number(e.target.value))}
                                disabled={submitting}
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {roleOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {role === 3 && (
                            <div className="space-y-2">
                                <Label>מערכות מורשות</Label>
                                {systems.length === 0 ? (
                                    <p className="text-xs text-gray-500">אין מערכות זמינות בתיק הלקוח.</p>
                                ) : (
                                    <div className="max-h-44 space-y-2 overflow-y-auto rounded-lg border border-gray-200 p-3">
                                        {systems.map((system) => (
                                            <label key={system.id} className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedSystemIds.includes(system.id)}
                                                    onChange={() =>
                                                        setSelectedSystemIds((current) =>
                                                            current.includes(system.id)
                                                                ? current.filter((id) => id !== system.id)
                                                                : [...current, system.id],
                                                        )
                                                    }
                                                    disabled={submitting}
                                                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                />
                                                <span>{system.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {error && (
                            <p className="text-xs text-red-600 flex items-center gap-1">
                                <ExclamationTriangleIcon className="w-3.5 h-3.5" />{error}
                            </p>
                        )}
                    </DialogBody>
                )}

                {!done && (
                    <DialogFooter className="flex gap-2 justify-end">
                        <Button variant="outline" onClick={handleClose}>ביטול</Button>
                        <Button onClick={handleSubmit} disabled={submitting}>
                            {submitting ? <Spinner size="sm" /> : 'שלח הזמנה'}
                        </Button>
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
}

function getCsrfToken(): string {
    const match = document.cookie.match(/csrftoken=([^;]+)/);
    return match ? match[1] : '';
}
